/**
 * Shared client-side image upload policy.
 *
 * One place for: the accepted formats, the size cap, the guidance line shown under every upload
 * field, and HEIC conversion.
 *
 * HEIC: iOS Safari usually transcodes a photo to JPEG when it is picked from a file input, but
 * Android browsers and "keep original" flows can still hand us a .heic file, which the server
 * cannot decode (prebuilt sharp has no libheif). We convert in the browser with heic2any, loaded
 * on demand so the ~50 KB WASM payload is only fetched when someone actually picks a HEIC.
 *
 * Mirrors the server policy in utils/imageNormalizer.js — keep MAX size and the wording in sync.
 */

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** The one line of guidance shown under every upload field (plan §10.6). */
export const IMAGE_UPLOAD_HINT =
  "JPG, PNG, WebP, GIF or HEIC · up to 5 MB · we optimise it for you";

/**
 * Keep `accept="image/*"` so phones offer the camera and gallery; the server validates by magic
 * bytes, so this is a hint, never a guarantee.
 */
export const IMAGE_ACCEPT = "image/*";

const HEIC_EXTENSION = /\.(heic|heif)$/i;
const HEIC_MIME = /^image\/hei[cf]/i;

export class ImageUploadPolicyError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "ImageUploadPolicyError";
    this.code = code;
  }
}

export function isHeicFile(file) {
  if (!file) return false;
  return HEIC_EXTENSION.test(file.name || "") || HEIC_MIME.test(file.type || "");
}

export function formatMegabytes(bytes) {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** True when the file looks like something the server will reject, so we can fail fast. */
export function getImageUploadBlockReason(file) {
  if (!file) return "No file selected.";
  if (file.size > MAX_UPLOAD_BYTES) {
    return `Images must be ${formatMegabytes(MAX_UPLOAD_BYTES)} or smaller.`;
  }
  if (file.type && !file.type.startsWith("image/") && !isHeicFile(file)) {
    return "That file is not an image.";
  }
  return null;
}

/**
 * Convert HEIC/HEIF to JPEG in the browser. Only called for HEIC files, so the WASM bundle is
 * fetched lazily. Returns the original file unchanged when no conversion is needed.
 */
export async function convertHeicToJpeg(file) {
  const imported = await import("heic2any");
  const convert = imported?.default || imported;

  const converted = await convert({
    blob: file,
    toType: "image/jpeg",
    quality: 0.9,
  });

  const blob = Array.isArray(converted) ? converted[0] : converted;
  const safeName = (file.name || "photo").replace(HEIC_EXTENSION, ".jpg");

  return new File([blob], safeName, { type: "image/jpeg" });
}

/**
 * Validate and prepare one file for upload. Use for every image field:
 *
 *   const prepared = await prepareImageForUpload(file);
 *   formData.append("image", prepared);
 *
 * Throws ImageUploadPolicyError with a user-facing message.
 */
export async function prepareImageForUpload(file) {
  const blockReason = getImageUploadBlockReason(file);
  if (blockReason) {
    throw new ImageUploadPolicyError("invalid_image", blockReason);
  }

  if (!isHeicFile(file)) return file;

  try {
    return await convertHeicToJpeg(file);
  } catch (error) {
    throw new ImageUploadPolicyError(
      "heic_conversion_failed",
      "We could not read that HEIC photo. Please retake it as a JPG, or update your app.",
    );
  }
}

/** Prepare several files, preserving order. */
export async function prepareImagesForUpload(files) {
  const prepared = [];
  for (const file of files) {
    prepared.push(await prepareImageForUpload(file));
  }
  return prepared;
}

/** Turn a caught error into a message safe to show the user. */
export function describeImageUploadError(error, fallback = "Image upload failed. Please try again.") {
  if (error instanceof ImageUploadPolicyError) return error.message;
  return error?.response?.data?.error || error?.response?.data?.message || fallback;
}
